import { router, Link } from '@inertiajs/react'
import { useState } from 'react'
import AppLayout from '../../components/AppLayout'
import { scoreColor, fmtScore } from '../../components/BreakdownScoreBar'
import { useTheme } from '../../contexts/ThemeContext'

// ────────────────────────────────────────────────────────────
// Utilities
// ────────────────────────────────────────────────────────────
function pct(v) { return v != null ? Math.round(v * 100) : null }
function pctStr(v) { return v != null ? `${Math.round(v * 100)}` : '—' }

// ────────────────────────────────────────────────────────────
// ScoreCell: thin horizontal bar + number
// ────────────────────────────────────────────────────────────
function ScoreCell({ value, C }) {
  if (value == null) return <span style={{ color: C.inkMuted, fontSize: '11px' }}>—</span>
  const p = Math.round(value * 100)
  const color = scoreColor(value, C)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '80px' }}>
      <div style={{ flex: 1, height: '6px', background: C.lineSoft, borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${p}%`, background: color, borderRadius: '3px' }} />
      </div>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color, width: '26px', textAlign: 'right' }}>{p}</span>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// 案A: 品目ランキングテーブル
// ────────────────────────────────────────────────────────────
function ItemRankingTable({ items, C }) {
  const [sort, setSort] = useState('delivery')
  const [limit, setLimit] = useState(30)

  const sorted = [...items].sort((a, b) => {
    if (sort === 'delivery') return (b.avg_delivery || 0) - (a.avg_delivery || 0)
    if (sort === 'appeal')   return (b.avg_appeal   || 0) - (a.avg_appeal   || 0)
    if (sort === 'count')    return b.count - a.count
    return 0
  })
  const shown = sorted.slice(0, limit)

  if (items.length === 0) {
    return <div style={{ color: C.inkMuted, textAlign: 'center', padding: '32px' }}>該当データなし</div>
  }

  const thStyle = (key) => ({
    padding: '10px 12px', textAlign: key === 'name' ? 'left' : 'center',
    fontSize: '10.5px', letterSpacing: '0.08em', color: C.inkMuted, fontWeight: 500,
    textTransform: 'uppercase', cursor: 'pointer', userSelect: 'none',
    background: sort === key ? C.paper2 : 'transparent',
    borderBottom: `1px solid ${C.line}`,
  })

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: C.paper2 }}>
              <th style={{ ...thStyle('rank'), width: '36px' }}>#</th>
              <th style={{ ...thStyle('name'), minWidth: '140px' }} onClick={() => setSort('name')}>品目</th>
              <th style={{ ...thStyle('category'), minWidth: '80px' }}>カテゴリ</th>
              <th style={{ ...thStyle('delivery'), minWidth: '120px' }} onClick={() => setSort('delivery')}>
                お届けスコア {sort === 'delivery' && '▾'}
              </th>
              <th style={{ ...thStyle('appeal'), minWidth: '120px' }} onClick={() => setSort('appeal')}>
                魅力スコア {sort === 'appeal' && '▾'}
              </th>
              <th style={{ ...thStyle('count'), width: '60px' }} onClick={() => setSort('count')}>
                回数 {sort === 'count' && '▾'}
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((item, i) => (
              <tr key={item.item_code} style={{
                borderBottom: `1px solid ${C.lineSoft}`,
                background: i % 2 === 0 ? 'transparent' : C.paper2,
              }}>
                <td style={{ padding: '10px 12px', textAlign: 'center', color: C.inkMuted, fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>
                  {String(i + 1).padStart(2, '0')}
                </td>
                <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                  <Link href={`/item_analysis/${item.item_code}`} style={{ color: C.ink, textDecoration: 'none' }}>
                    {item.name}
                  </Link>
                </td>
                <td style={{ padding: '10px 12px', fontSize: '11px', color: C.inkMuted }}>
                  {item.middle_category_code || '—'}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <ScoreCell value={item.avg_delivery} C={C} />
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <ScoreCell value={item.avg_appeal} C={C} />
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted }}>
                  {item.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length > limit && (
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <button onClick={() => setLimit(l => l + 30)} style={{
            fontFamily: 'inherit', fontSize: '12px', padding: '6px 18px',
            border: `1px solid ${C.line}`, borderRadius: '5px', background: C.paper2,
            color: C.inkSoft, cursor: 'pointer',
          }}>
            さらに表示（残 {sorted.length - limit} 件）
          </button>
        </div>
      )}
      <div style={{ marginTop: '8px', fontSize: '11px', color: C.inkMuted, textAlign: 'right' }}>
        計 {items.length} 品目
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// 案B: 品目×評価軸ヒートマップ
// ────────────────────────────────────────────────────────────
const HEAT_COLORS = [
  { min: 0.00, max: 0.40, from: [200, 50, 50],  to: [220, 80, 60]  },
  { min: 0.40, max: 0.60, from: [220, 160, 80], to: [200, 180, 90] },
  { min: 0.60, max: 0.80, from: [160, 200, 120],to: [100, 180, 100]},
  { min: 0.80, max: 1.00, from: [60, 150, 80],  to: [40, 120, 60]  },
]

function heatColor(v) {
  if (v == null) return 'transparent'
  const band = HEAT_COLORS.find(b => v <= b.max) || HEAT_COLORS[HEAT_COLORS.length - 1]
  const t = Math.max(0, Math.min(1, (v - band.min) / (band.max - band.min)))
  const r = Math.round(band.from[0] + (band.to[0] - band.from[0]) * t)
  const g = Math.round(band.from[1] + (band.to[1] - band.from[1]) * t)
  const b = Math.round(band.from[2] + (band.to[2] - band.from[2]) * t)
  return `rgb(${r},${g},${b})`
}

function CriterionHeatmap({ items, criteriaMeta, C }) {
  const [topN, setTopN] = useState(25)
  const shown = items.slice(0, topN)

  if (items.length === 0) {
    return <div style={{ color: C.inkMuted, textAlign: 'center', padding: '32px' }}>該当データなし</div>
  }

  const CELL = 36
  const NAME_W = 140

  return (
    <div>
      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '600px' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '11px', tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: C.paper2 }}>
            <tr>
              <th style={{ width: NAME_W, padding: '8px 10px', textAlign: 'left', color: C.inkMuted, fontWeight: 500, borderBottom: `1px solid ${C.line}` }}>
                品目
              </th>
              {criteriaMeta.map(c => (
                <th key={c.key} style={{ width: CELL, padding: '4px 2px', textAlign: 'center', color: C.inkMuted, fontWeight: 400, borderBottom: `1px solid ${C.line}`, writingMode: 'vertical-rl', whiteSpace: 'nowrap', fontSize: '10px' }}>
                  {c.name}
                </th>
              ))}
              <th style={{ width: 50, padding: '8px 6px', textAlign: 'center', color: C.inkMuted, fontWeight: 500, borderBottom: `1px solid ${C.line}`, fontSize: '10px' }}>
                回数
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((item, i) => (
              <tr key={item.item_code} style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
                <td style={{ padding: '6px 10px', fontWeight: 500, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: NAME_W }}>
                  {item.name}
                </td>
                {criteriaMeta.map(c => {
                  const v = item.scores[c.key]
                  const bg = heatColor(v)
                  return (
                    <td key={c.key} title={`${item.name} × ${c.name}: ${v != null ? Math.round(v * 100) + '%' : '—'}`}
                      style={{ width: CELL, height: '30px', background: bg, textAlign: 'center', cursor: 'default' }}>
                      {v != null && (
                        <span style={{ fontSize: '9px', color: '#fff', fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                          {Math.round(v * 100)}
                        </span>
                      )}
                    </td>
                  )
                })}
                <td style={{ padding: '6px', textAlign: 'center', color: C.inkMuted, fontFamily: "'JetBrains Mono', monospace" }}>
                  {item.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
        {items.length > topN ? (
          <button onClick={() => setTopN(n => n + 25)} style={{
            fontFamily: 'inherit', fontSize: '12px', padding: '5px 14px',
            border: `1px solid ${C.line}`, borderRadius: '5px', background: C.paper2,
            color: C.inkSoft, cursor: 'pointer',
          }}>
            さらに表示（残 {items.length - topN} 件）
          </button>
        ) : <span />}
        {/* 凡例 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: C.inkMuted }}>
          <span>低</span>
          {[0, 0.3, 0.5, 0.7, 0.9].map(v => (
            <span key={v} style={{ display: 'inline-block', width: '20px', height: '10px', background: heatColor(v), borderRadius: '2px' }} />
          ))}
          <span>高</span>
        </div>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// 案F: 共起ランキング
// ────────────────────────────────────────────────────────────
function CooccurrenceRanking({ data, C }) {
  if (!data || data.length === 0) return <div style={{ color: C.inkMuted, textAlign: 'center', padding: '32px' }}>該当データなし</div>
  const maxCnt = Math.max(...data.map(r => r.count), 1)
  return (
    <div>
      <div style={{ fontSize: '11.5px', color: C.inkMuted, marginBottom: '12px' }}>
        同じセットに一緒に入った品目ペアを出現回数順で表示。品目名クリックで詳細へ。
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: C.paper2, borderBottom: `1px solid ${C.line}` }}>
              {['#', '品目A', '品目B', '共起回数'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: h === '共起回数' ? 'center' : 'left', fontSize: '10.5px', color: C.inkMuted, fontWeight: 500, letterSpacing: '0.08em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.lineSoft}`, background: i % 2 === 0 ? 'transparent' : C.paper2 }}>
                <td style={{ padding: '9px 14px', color: C.inkMuted, fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', width: '36px' }}>
                  {String(i + 1).padStart(2, '0')}
                </td>
                <td style={{ padding: '9px 14px' }}>
                  <Link href={`/item_analysis/${r.item_a.code}`} style={{ color: C.ink, textDecoration: 'none', fontWeight: 500 }}>
                    {r.item_a.name}
                  </Link>
                </td>
                <td style={{ padding: '9px 14px' }}>
                  <Link href={`/item_analysis/${r.item_b.code}`} style={{ color: C.ink, textDecoration: 'none', fontWeight: 500 }}>
                    {r.item_b.name}
                  </Link>
                </td>
                <td style={{ padding: '9px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ flex: 1, height: '8px', background: C.lineSoft, borderRadius: '4px', overflow: 'hidden', maxWidth: '200px' }}>
                      <div style={{ height: '100%', width: `${(r.count / maxCnt) * 100}%`, background: C.greenDark, borderRadius: '4px' }} />
                    </div>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: C.inkMuted, width: '32px', textAlign: 'right' }}>
                      {r.count}回
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function ItemAnalysisShow({
  period_type, period_value, period_label, available_periods,
  prev_period, next_period,
  min_count, middle_category_code,
  categories, item_ranking, criterion_heatmap, cooccurrence, criteria_meta,
}) {
  const { theme: C } = useTheme()
  const [activeTab, setActiveTab] = useState('ranking')

  const navigate = (overrides) => {
    router.get('/item_analysis', {
      period_type,
      period_value,
      min_count,
      middle_category_code: middle_category_code || '',
      ...overrides,
    }, { preserveScroll: true })
  }

  // 期間タイプを変えたとき、その期間タイプの最新値に飛ぶ（バックエンドが補正）
  const switchPeriodType = (t) => {
    router.get('/item_analysis', {
      period_type: t,
      min_count,
      middle_category_code: middle_category_code || '',
    }, { preserveScroll: true })
  }

  const stepperBtnStyle = (enabled) => ({
    fontFamily: 'inherit', fontSize: '14px', fontWeight: 600,
    width: '28px', height: '30px', borderRadius: '4px',
    border: `1px solid ${C.line}`, background: C.paper2,
    color: enabled ? C.inkSoft : C.inkMuted,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.4,
    padding: 0,
  })

  const sectionStyle = {
    background: C.paper, borderRadius: '12px', border: `1px solid ${C.line}`,
    boxShadow: '0 4px 14px rgba(60,50,30,0.07)', overflow: 'hidden',
  }
  const sectionHeader = {
    padding: '16px 20px', borderBottom: `1px solid ${C.lineSoft}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  }

  return (
    <AppLayout bgColor={C.bg} maxWidth="1300px">
      {/* ── ツールバー ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
        paddingBottom: '18px', borderBottom: `1px solid ${C.line}`, marginBottom: '28px',
      }}>
        <span style={{ fontSize: '18px', fontWeight: 700, color: C.ink, fontFamily: "'Noto Serif JP', serif" }}>
          品目分析
        </span>

        {/* 期間タイプ切替 */}
        <div style={{ display: 'inline-flex', borderRadius: '5px', overflow: 'hidden', border: `1px solid ${C.line}` }}>
          {[
            { id: 'year',    label: '年度' },
            { id: 'quarter', label: '四半期' },
            { id: 'month',   label: '月次' },
          ].map(t => (
            <button key={t.id} onClick={() => switchPeriodType(t.id)}
              style={{
                fontFamily: 'inherit', fontSize: '12px', fontWeight: 500,
                padding: '6px 12px', border: 'none', cursor: 'pointer',
                background: period_type === t.id ? C.greenDark : C.paper2,
                color: period_type === t.id ? C.paper : C.inkSoft,
              }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* 期間ステッパー: ◀ 期間ドロップダウン ▶ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            disabled={!prev_period}
            onClick={() => prev_period && navigate({ period_value: prev_period })}
            title="前の期間"
            style={stepperBtnStyle(!!prev_period)}>
            ◀
          </button>
          <select value={period_value || ''} onChange={e => navigate({ period_value: e.target.value })}
            style={{ fontFamily: 'inherit', fontSize: '14px', fontWeight: 500, padding: '5px 28px 5px 10px', background: C.paper2, border: `1px solid ${C.line}`, borderRadius: '4px', color: C.ink, cursor: 'pointer', appearance: 'none', minWidth: '160px' }}>
            {(available_periods || []).map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button
            disabled={!next_period}
            onClick={() => next_period && navigate({ period_value: next_period })}
            title="次の期間"
            style={stepperBtnStyle(!!next_period)}>
            ▶
          </button>
        </div>

        {/* カテゴリ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: C.inkMuted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>カテゴリ</span>
          <select value={middle_category_code || ''} onChange={e => navigate({ middle_category_code: e.target.value || '' })}
            style={{ fontFamily: 'inherit', fontSize: '13px', padding: '5px 28px 5px 10px', background: C.paper2, border: `1px solid ${C.line}`, borderRadius: '4px', color: C.ink, cursor: 'pointer', appearance: 'none' }}>
            <option value="">すべて</option>
            {(categories || []).map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>

        {/* 最低出現回数 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: C.inkMuted, letterSpacing: '0.12em', textTransform: 'uppercase' }}>最低出現</span>
          <select value={min_count} onChange={e => navigate({ min_count: e.target.value })}
            style={{ fontFamily: 'inherit', fontSize: '13px', padding: '5px 28px 5px 10px', background: C.paper2, border: `1px solid ${C.line}`, borderRadius: '4px', color: C.ink, cursor: 'pointer', appearance: 'none' }}>
            {[1, 2, 3, 5, 10].map(n => <option key={n} value={n}>{n}回以上</option>)}
          </select>
        </div>

        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '12px', color: C.inkMuted }}>{item_ranking.length} 品目</span>
      </div>

      {/* ── タブ ── */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
        {[
          { id: 'ranking',      label: '品目ランキング（案A）' },
          { id: 'heatmap',      label: '評価軸ヒートマップ（案B）' },
          { id: 'cooccurrence', label: '共起ランキング（案F）' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              fontFamily: 'inherit', fontSize: '13px', fontWeight: activeTab === tab.id ? 600 : 400,
              padding: '7px 18px', borderRadius: '6px', cursor: 'pointer',
              border: `1px solid ${activeTab === tab.id ? C.greenDark : C.line}`,
              background: activeTab === tab.id ? C.greenDark : C.paper,
              color: activeTab === tab.id ? C.paper : C.inkSoft,
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── 案A: 品目ランキング ── */}
      {activeTab === 'ranking' && (
        <div style={sectionStyle}>
          <div style={sectionHeader}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: C.ink }}>品目別スコアランキング</div>
              <div style={{ fontSize: '11.5px', color: C.inkMuted, marginTop: '3px' }}>
                「その品目が入ったセット」のお届け・魅力スコアの平均。列ヘッダクリックで並び替え。
              </div>
            </div>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <ItemRankingTable items={item_ranking} C={C} />
          </div>
        </div>
      )}

      {/* ── 案B: ヒートマップ ── */}
      {activeTab === 'heatmap' && (
        <div style={sectionStyle}>
          <div style={sectionHeader}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: C.ink }}>品目×評価軸ヒートマップ</div>
              <div style={{ fontSize: '11.5px', color: C.inkMuted, marginTop: '3px' }}>
                品目が含まれるセットでの各評価軸の平均調整スコア。全体平均が高い順に並ぶ。
              </div>
            </div>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <CriterionHeatmap items={criterion_heatmap} criteriaMeta={criteria_meta} C={C} />
          </div>
        </div>
      )}

      {/* ── 案F: 共起ランキング ── */}
      {activeTab === 'cooccurrence' && (
        <div style={sectionStyle}>
          <div style={sectionHeader}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: C.ink }}>共起ランキング</div>
              <div style={{ fontSize: '11.5px', color: C.inkMuted, marginTop: '3px' }}>
                同じセットによく一緒に登場する品目ペアの上位50組。
              </div>
            </div>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <CooccurrenceRanking data={cooccurrence} C={C} />
          </div>
        </div>
      )}
    </AppLayout>
  )
}
